import React from 'react';
import { 
  ExclamationTriangleIcon, 
  InformationCircleIcon, 
  XCircleIcon,
  ArrowPathIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { getErrorMessage, isSubscriptionLimitError, isFileError } from '../utils/errorMessages';

interface ErrorDisplayProps {
  error: any;
  onRetry?: () => void;
  onUpgrade?: () => void;
  className?: string;
  compact?: boolean;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  onUpgrade,
  className = '',
  compact = false
}) => {
  const errorConfig = getErrorMessage(error);
  const isSubscriptionError = isSubscriptionLimitError(error);
  const isFileRelatedError = isFileError(error);

  // Choose appropriate icon based on error type
  const getIcon = () => {
    if (isSubscriptionError) {
      return <SparklesIcon className="h-5 w-5 text-amber-500" />;
    }
    if (isFileRelatedError) {
      return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
    return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
  };

  // Choose appropriate colors based on error type
  const getColorClasses = () => {
    if (isSubscriptionError) {
      return {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-700',
        title: 'text-amber-800 dark:text-amber-200',
        message: 'text-amber-700 dark:text-amber-300',
        button: 'bg-amber-600 hover:bg-amber-700 text-white'
      };
    }
    if (isFileRelatedError) {
      return {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-700',
        title: 'text-blue-800 dark:text-blue-200',
        message: 'text-blue-700 dark:text-blue-300',
        button: 'bg-blue-600 hover:bg-blue-700 text-white'
      };
    }
    return {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-700',
      title: 'text-red-800 dark:text-red-200',
      message: 'text-red-700 dark:text-red-300',
      button: 'bg-red-600 hover:bg-red-700 text-white'
    };
  };

  const colors = getColorClasses();

  if (compact) {
    return (
      <div className={`flex items-start space-x-3 p-3 rounded-lg ${colors.bg} ${colors.border} border ${className}`}>
        <div className="flex-shrink-0 mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${colors.title}`}>
            {errorConfig.title}
          </p>
          <p className={`text-sm ${colors.message} mt-1`}>
            {errorConfig.message}
          </p>
          {errorConfig.suggestion && (
            <p className={`text-xs ${colors.message} mt-1 opacity-90`}>
              💡 {errorConfig.suggestion}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg ${colors.bg} ${colors.border} border p-4 ${className}`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="flex-1">
          <h3 className={`text-sm font-semibold ${colors.title}`}>
            {errorConfig.title}
          </h3>
          <div className="mt-2">
            <p className={`text-sm ${colors.message}`}>
              {errorConfig.message}
            </p>
            {errorConfig.suggestion && (
              <p className={`text-sm ${colors.message} mt-2 opacity-90`}>
                <span className="font-medium">💡 Dica:</span> {errorConfig.suggestion}
              </p>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            {isSubscriptionError && onUpgrade && (
              <button
                onClick={onUpgrade}
                className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${colors.button} transition-colors`}
              >
                <SparklesIcon className="h-4 w-4 mr-2" />
                {errorConfig.action || 'Ver Planos Premium'}
              </button>
            )}
            
            {onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                {errorConfig.action === 'Tentar novamente' ? errorConfig.action : 'Tentar Novamente'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;